import React, { useContext } from 'react';
import { X, Calendar, Zap, Bug, Star, Info } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { I18nContext } from '../../i18n/context/I18nProvider';

const typeIcons = {
  feature: Zap,
  improvement: Star,
  bugfix: Bug,
  breaking: Info
};

const typeColors = {
  feature: 'text-green-600 bg-green-50',
  improvement: 'text-blue-600 bg-blue-50',
  bugfix: 'text-orange-600 bg-orange-50',
  breaking: 'text-red-600 bg-red-50'
};

interface ChangelogEntry {
  date: string;
  changes: {
    [changeId: string]: {
      type: string;
      description: string;
    };
  };
}

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
  lastSeenVersion?: string | null;
}

export function ChangelogModal({ isOpen, onClose, lastSeenVersion }: ChangelogModalProps) {
  const { t } = useTranslation();
  const context = useContext(I18nContext);

  if (!isOpen) return null;

  // Récupérer les entrées changelog depuis le contexte
  const changelogEntries: Record<string, ChangelogEntry> = context?.messages?.changelog?.entries || {};
  const versions = Object.keys(changelogEntries).sort((a, b) => b.localeCompare(a));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-900">
              {t('changelog.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-1 hover:bg-gray-100"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Content scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {versions.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              {t('changelog.noUpdates')}
            </div>
          ) : (
            versions.map((version) => {
              const entry = changelogEntries[version];
              const isNew = lastSeenVersion && version > lastSeenVersion;
              
              return (
                <div 
                  key={version}
                  className={`border p-4 ${
                    isNew ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  {/* Header version */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        v{version}
                      </h3>
                      {isNew && (
                        <span className="text-xs bg-blue-500 text-white px-2 py-1">
                          {t('changelog.newLabel')}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">{entry.date}</span>
                  </div>
                  
                  {/* Changes list */}
                  <div className="space-y-2">
                    {Object.entries(entry.changes || {}).map(([changeId, change]) => {
                      const Icon = typeIcons[change.type as keyof typeof typeIcons];
                      const colorClass = typeColors[change.type as keyof typeof typeColors];
                      
                      return (
                        <div key={changeId} className="flex items-start gap-3">
                          <div className={`p-1 ${colorClass}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <span className="text-sm text-gray-700 flex-1">
                            {change.description}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}